import { useMutation } from '@graphcommerce/graphql'
import {
  CustomizableProductInterface,
  FourOverCustomOptionModifier,
  PROOFING_OPTION,
  ProductInterface,
  SimpleProduct,
} from '@graphcommerce/graphql-mesh'
import {
  ApolloCartErrorAlert,
  useClearCurrentCartId,
  useFormGqlMutationCart,
} from '@graphcommerce/magento-cart'
import { CartItemFragment } from '@graphcommerce/magento-cart-items'
import { OptionTypeRenderer } from '@graphcommerce/magento-product'
import { ProductCustomizableFragment } from '@graphcommerce/magento-product/components/ProductCustomizable/ProductCustomizable.gql'
import { ConfigurableOptionsInputProps } from '@graphcommerce/magento-product-configurable/ConfigurableOptions/ConfigurableOptions'
import { ConfigurableProductAddToCartMutationVariables } from '@graphcommerce/magento-product-configurable/graphql/ConfigurableProductAddToCart.gql'
import { Money } from '@graphcommerce/magento-store'
import { extendableComponent } from '@graphcommerce/next-ui'
import { i18n } from '@lingui/core'
import { Alert, Box, SxProps, Theme, SwipeableDrawer, MenuItem } from '@mui/material'
import React, { useCallback, useEffect, useState, useContext, useMemo } from 'react'
import { AttachArtworkDocument } from '../Artwork/AttachArtwork.gql'
import { PrimaryButton, SecondaryButton } from '../Button'
import { StandardDropdown } from '../Dropdown/StandardDropdown'
import { fbqAddToCart } from '../FacebookPixel/events/fbqAddToCart'
import { FileUploadModal, ProofOptionEnum, s3File } from '../FileUpload/FileUploadModal'
import { gtagAddToCart } from '../GoogleAnalytics/events/gtagAddToCart'
import { klaviyoAddToCart } from '../Klaviyo/events/klaviyoAddToCart'
import { globalContext } from '../NextUi/globalContext'
import { PaypalPayLater } from '../PaymentBraintree/PaypalPayLater/PaypalPayLater'
import { AttachProjectDataDocument } from '../PitchPrint/AttachProjectData.gql'
import { useConfigurableContext } from '../ProductConfigurable/ConfigurableContext/ConfigurableContext'
import { ConfigurableOptionsInput } from '../ProductConfigurable/ConfigurableOptions/ConfigurableOptions'
import { ProductConfigurationGrid } from '../ProductConfigurable/ProductConfigurationGrid'
import { SimpleProductAddToCartDocument } from '../ProductSimple/SimpleProductAddToCart.gql'
import { ErrorPrompt } from '../Prompt/ErrorPrompt'
import { ArtworkRequirements } from './Artwork/ArtworkRequirements'
import { AddedToCart } from './Minicart/AddedToCart'
import { ProductCustomizable } from './ProductCustomizable/ProductCustomizable'

type ConfigurableProductAddToCartProps = {
  variables: Omit<ConfigurableProductAddToCartMutationVariables, 'cartId' | 'selectedOptions'>
  product: Pick<
    ProductInterface,
    | 'name'
    | 'sku'
    | 'requires_artwork'
    | 'categories'
    | 'uid'
    | 'design_id'
    | 'setup_price'
    | 'setup_price_discount'
    | 'setup_price_quantity_threshold'
    | 'setup_price_type'
    | 'price_range'
    | 'fourover_options_modifier'
  >
  name: string
  optionEndLabels?: Record<string, React.ReactNode>
  children?: React.ReactNode
  additionalButtons?: React.ReactNode
  sx?: SxProps<Theme>
  optionsProps?: Omit<
    ConfigurableOptionsInputProps,
    'name' | 'sku' | 'control' | 'rules' | 'errors' | 'optionEndLabels'
  >
  callback?: () => void
}

const compName = 'ConfigurableOptionsInput' as const
const parts = ['form', 'button', 'finalPrice', 'quantity', 'divider', 'buttonWrapper'] as const
const { classes } = extendableComponent(compName, parts)

export function FourOverConfProductAddToCart(props: ConfigurableProductAddToCartProps) {
  const {
    name,
    children,
    variables,
    optionEndLabels,
    optionsProps,
    additionalButtons,
    product,
    sx = [],
    callback,
    ...buttonProps
  } = props

  const [attachArtwork] = useMutation(AttachArtworkDocument)
  const [attachProjectData] = useMutation(AttachProjectDataDocument)
  const [artworkFiles, setArtworkFiles] = useState<Array<s3File>>([])
  const [ppProjectId, setPpProjectId] = useState<null | string>(null)
  const [isDisplayFileUploadModal, setIsDisplayFileUploadModal] = useState<boolean>(false)
  const { drawer, setDrawer } = useContext(globalContext)
  const [addedToCartDrawer, setAddedToCartDrawer] = useState<boolean | null>(null)
  const [shouldRetry, setShouldRetry] = useState(false)

  const [isDisplayErrorPrompt, setIsDisplayErrorPrompt] = useState<boolean>(false)
  const [attachError, setAttachError] = useState<Error>()
  const clearCurrentCartId = useClearCurrentCartId()
  const { getVariants, selection } = useConfigurableContext(variables.sku)
  const selectedVariant = getVariants(selection)
  const selectedVariantDesignId = selectedVariant?.at(0)?.product?.design_id
  const requiresArtwork = !selectedVariantDesignId && product.requires_artwork
  const [artworkComment, setArtworkComment] = useState('')
  const [proofOption, setProofOption] = useState<ProofOptionEnum>(ProofOptionEnum.wait_for_proof)

  const handleUploadArtwork = () => {
    setIsDisplayFileUploadModal(true)
  }

  const form = useFormGqlMutationCart(SimpleProductAddToCartDocument, {
    defaultValues: { ...variables },
    onBeforeSubmit: ({ selectedOptions, ...vars }) => {
      if (
        !selectedVariant ||
        !selectedVariant?.at(0)?.product?.sku ||
        (requiresArtwork && !artworkFiles.length) ||
        (selectedVariantDesignId && !ppProjectId)
      ) {
        return false
      }

      const productToAdd = selectedVariant?.at(0)?.product || product

      // We replace option value uids with ids, coz backend does not work with uids. We could just work with uids in
      // frontend, but 1. some things are already done with uis by default 2. we think backend will change those to uids in the near future
      const enteredOptions = vars.enteredOptions
        ?.map((option) => {
          if (
            option &&
            option.value &&
            option?.value.length > 8 &&
            'options' in productToAdd &&
            Array.isArray(productToAdd.options)
          ) {
            // This means uid is used instead of id
            const optionData = productToAdd.options?.find((op) => op.uid === option.uid)
            if (
              optionData &&
              'dropdownValue' in optionData &&
              Array.isArray(optionData.dropdownValue)
            ) {
              const valueData = optionData.dropdownValue.find((v) => v.uid === option.value)
              if (valueData) {
                return {
                  ...option,
                  value: valueData.option_type_id.toString(),
                }
              }
            }
          }
          return { uid: option?.uid || '', value: false }
        })
        .filter((o) => o.value !== false)

      return {
        ...vars,
        sku: selectedVariant?.at(0)?.product?.sku as string,
        enteredOptions,
        quantity: 1,
      }
    },
    onComplete: ({ data, errors }, vars) => {
      // When we have an invalid cartId (maybe they have been previously signIn as a customer, now the customer token is expired, but cartId is still there)
      if (
        errors?.find((error) => error.message.indexOf('user cannot perform operations on cart') > 0)
      ) {
        clearCurrentCartId()
        setShouldRetry(true)
        return
      }

      if (data?.addProductsToCart?.user_errors.length) {
        return
      }

      const addedItem = data?.addProductsToCart?.cart.items?.slice(-1)[0]

      gtagAddToCart(addedItem as unknown as CartItemFragment, 1, { added_from: 'product_page' })

      const category = product.categories?.length
        ? product.categories?.at(product.categories.length - 1)?.name
        : ''

      fbqAddToCart(addedItem as unknown as CartItemFragment, 1, category || '')
      klaviyoAddToCart(addedItem as unknown as CartItemFragment, 1, category || '')

      const cartItemUid = data?.addProductsToCart?.cart.items?.slice(-1)[0]?.uid ?? ''

      if (data && artworkFiles && artworkFiles.length > 0) {
        artworkFiles.forEach(async (artworkFile) => {
          await attachArtwork({
            variables: {
              cartItemUid,
              fileName: artworkFile.fileName || '',
              fileUrl: artworkFile.url || '',
              extra: JSON.stringify({ type: artworkFile.type || '' }),
              proofingOption:
                (proofOption.toString() as PROOFING_OPTION) ?? ('' as PROOFING_OPTION),
              comments: artworkComment ?? '',
            },
          })
            .catch((error: Error) => {
              setAttachError(error)
              setIsDisplayErrorPrompt(true)
            })
            .finally(() => setArtworkFiles([]))
        })
      }
      if (data && ppProjectId) {
        attachProjectData({
          variables: {
            cartItemUid,
            cartUid: data.addProductsToCart?.cart.id as string,
            projectId: ppProjectId,
          },
        })
          .then(() => {
            setPpProjectId(null)
          })
          .catch((error: Error) => {})
      }
    },
  })

  const { handleSubmit, formState, required, control, error, data, setValue, watch } = form
  const submitHandler = handleSubmit(() => {})

  useEffect(() => {
    if (shouldRetry) {
      setShouldRetry(false)
      submitHandler()
        .then(() => {})
        .catch(() => {})
    }
  }, [shouldRetry, submitHandler])

  useEffect(() => {
    if (formState.isSubmitting) {
      setAddedToCartDrawer(null)
      setDrawer({ id: null })
    }
  }, [formState.isSubmitting, setDrawer, setAddedToCartDrawer])

  useEffect(() => {
    if (
      !formState.isSubmitting &&
      formState.isSubmitSuccessful &&
      !error?.message &&
      !data?.addProductsToCart?.user_errors?.length &&
      drawer.id === null &&
      addedToCartDrawer === null &&
      (!product.requires_artwork || artworkFiles.length || ppProjectId)
    ) {
      setDrawer({ id: 'added_to_cart' })
      setAddedToCartDrawer(true)
    }
  }, [
    data,
    error,
    formState,
    drawer,
    setDrawer,
    addedToCartDrawer,
    setAddedToCartDrawer,
    product,
    artworkFiles,
    ppProjectId,
  ])

  const enteredOptions = watch('enteredOptions')

  const colorSpecOption = (
    (selectedVariant ? selectedVariant.at(0)?.product : product) as CustomizableProductInterface
  )?.options?.find((p) => p?.title === 'Colorspec')
  const colorSpecOptionValue =
    enteredOptions && colorSpecOption
      ? (
          colorSpecOption as React.ComponentProps<OptionTypeRenderer['CustomizableDropDownOption']>
        )?.dropdownValue?.find(
          (v) =>
            v?.uid ===
            (enteredOptions || []).find((eo) => eo && eo.uid === colorSpecOption.uid)?.value,
        )
      : null

  // const action = useAddProductsToCartAction(props)
  // For 4Over, we store runsizes (quantities) with colorSpecs, because they 2 together define the baseprice
  // But we want to show them in nice way, in seperate dropdowns anyways
  const optionsAdjusted = useMemo(() => {
    const selectedColorspecTitle = colorSpecOptionValue?.title?.split(' - ')?.at(0)
    const quantites: Record<number, number> = {}
    const p = selectedVariant?.at(0)?.product
    if (p && 'options' in p && Array.isArray(p.options)) {
      const options = p.options.map((option) => {
        if (option.title === 'Colorspec') {
          let values = []
          if (option.dropdownValue) {
            values = option.dropdownValue
              .filter((v) => v.price)
              .map((v) => {
                const valueParts = v.title.split(' - ')
                if (valueParts.length > 1) {
                  if (!selectedColorspecTitle || selectedColorspecTitle === valueParts[0]) {
                    quantites[parseInt(valueParts[1] as string, 10)] = v.price
                  }
                  return {
                    ...v,
                    title: valueParts[0],
                  }
                }
                return v
              })
          }
          return {
            ...option,
            dropdownValue: values,
          }
        }
        return option
      })
      return {
        options,
        quantities: Object.fromEntries(
          Object.entries(quantites).sort((a, b) => a[0].localeCompare(b[0])),
        ),
      }
    }
    return {
      options: [],
      quantities: {},
    }
  }, [selectedVariant, colorSpecOptionValue])

  const optionSelectionDetails = useMemo(() => {
    const selectedVariantOptions = (
      (selectedVariant ? selectedVariant.at(0)?.product : product) as CustomizableProductInterface
    )?.options
    const enteredOptionDetails = enteredOptions?.map((option) => {
      const selectedOption = selectedVariantOptions?.find((o) => o?.uid === option?.uid)
      if (!selectedOption) {
        return null
      }
      const selectedValue = (
        selectedOption as React.ComponentProps<OptionTypeRenderer['CustomizableDropDownOption']>
      ).dropdownValue?.find((v) => v?.uid === option?.value)
      if (!selectedValue) {
        return null
      }
      return {
        title: selectedOption.title,
        value: selectedValue.title,
        price: selectedValue.price,
      }
    })
    return enteredOptionDetails
  }, [enteredOptions, selectedVariant, product])

  const totalPrice = useMemo(
    () => optionSelectionDetails?.reduce((acc, option) => (acc || 0) + (option?.price || 0), 0),
    [optionSelectionDetails],
  )

  const quantityDropdownOptions = useMemo(
    () =>
      (optionsAdjusted &&
        Object.keys(optionsAdjusted.quantities).map((qty) => {
          const totalQtyPrice = optionsAdjusted.quantities[qty]
          const unitPrice = totalQtyPrice / parseInt(qty, 10)
          const price = parseFloat(unitPrice.toFixed(unitPrice >= 1 ? 2 : 3))

          return {
            value: qty || '',
            labels: [qty?.toLocaleString() || '', price || 0],
            price,
            totalQtyPrice,
          }
        })) ||
      [],
    [optionsAdjusted],
  )
  // Autoselect the minimum quantity if no quantity is specified
  const quantity = watch('quantity')
  useEffect(() => {
    if (!quantity && colorSpecOptionValue && optionsAdjusted.quantities) {
      const keys = Object.keys(optionsAdjusted.quantities)
      const minQuantity = keys.reduce((min, k) => (Number(k) < Number(min) ? k : min))
      if (minQuantity) {
        setValue('quantity', Number.parseInt(minQuantity, 10))
      }
    }
  }, [quantity, optionsAdjusted, setValue, colorSpecOptionValue])

  return (
    <>
      <Box
        component='form'
        onSubmit={submitHandler}
        noValidate
        className={classes.form}
        sx={[
          {
            width: '100%',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <div className='mb-[15px] mt-[10px] h-[1px] bg-20-grey' />
        <ProductConfigurationGrid sku={variables.sku} />
        <ConfigurableOptionsInput
          name='selectedOptions'
          sku={variables.sku}
          control={control}
          rules={{ required: required.selectedOptions }}
          errors={formState.errors}
          optionEndLabels={optionEndLabels}
          product={product}
          {...optionsProps}
        />

        <ProductCustomizable
          product={
            { ...(selectedVariant ? selectedVariant.at(0)?.product : product) } as SimpleProduct &
              ProductCustomizableFragment
          }
          fouroverOptionModifiers={
            (product?.fourover_options_modifier as FourOverCustomOptionModifier[]) || []
          }
          form={form}
          selectedVariantSku=''
          quantities={optionsAdjusted.quantities}
          colorSpecOptionValue={colorSpecOptionValue}
          filter={(option) => option.title !== 'Turn Around Time'}
        />

        <div className='flex flex-col gap-[15px]'>
          {'quantities' in optionsAdjusted && colorSpecOptionValue && (
            <div className='my-3.75'>
              <p className='mb-3 text-[14px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
                Pricing
              </p>
              <div className='rounded-[14px] bg-[rgba(120,120,128,0.08)]'>
                {quantityDropdownOptions.map((option) => {
                  const qty = Number(option.value)
                  const decimals = option.price >= 1 ? 2 : 3
                  const unitPriceStr = option.price.toLocaleString('en-US', {
                    minimumFractionDigits: decimals,
                    maximumFractionDigits: decimals,
                  })
                  const totalStr = (option.price * qty).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                  const isSelected = watch('quantity')?.toString() === option.value
                  return (
                    <button
                      key={option.value}
                      type='button'
                      onClick={() => setValue('quantity', qty)}
                      className={`flex w-full items-center rounded-[4px] px-4 py-3.5 transition-all active:scale-[0.99] ${
                        isSelected
                          ? 'bg-[#D1CBCB33] shadow-[0px_2px_3px_0px_rgba(0,0,0,0.25)] ring-[1.6px] ring-inset ring-[#5D54DE]'
                          : ''
                      }`}
                    >
                      <span className={`flex-1 text-left text-[15px] text-[#1C1C1E] ${isSelected ? 'font-bold' : 'font-medium'}`}>
                        {qty.toLocaleString()}
                      </span>
                      <span className={`flex-1 text-center text-[15px] text-[#1C1C1E] ${isSelected ? 'font-bold' : ''}`}>
                        ${unitPriceStr} ea
                      </span>
                      <span className={`flex-1 text-right text-[15px] text-[#1C1C1E] ${isSelected ? 'font-bold' : ''}`}>
                        ${totalStr}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}
          <ProductCustomizable
            product={
              { ...(selectedVariant ? selectedVariant.at(0)?.product : product) } as SimpleProduct &
                ProductCustomizableFragment
            }
            fouroverOptionModifiers={
              (product?.fourover_options_modifier as FourOverCustomOptionModifier[]) || []
            }
            form={form}
            selectedVariantSku=''
            quantities={optionsAdjusted.quantities}
            colorSpecOptionValue={colorSpecOptionValue}
            filter={(option) => option.title === 'Turn Around Time'}
          />
        </div>
        {!!colorSpecOptionValue && (
          <div className='mt-[15px] flex flex-col gap-3 bg-black/3 px-5 py-3'>
            <div className='Type-XL-Medium flex items-center justify-between'>
              <span>Total</span>
              <span>
                <Money
                  value={totalPrice}
                  currency={product?.price_range?.minimum_price?.regular_price?.currency}
                />
              </span>
            </div>
          </div>
        )}

        <div className='mt-[15px] h-[1px] bg-20-grey' />
        {children}

        {totalPrice && <PaypalPayLater totalPrice={totalPrice} placement='product' />}

        {requiresArtwork && (
          <div className='mb-5 mt-5'>
            {artworkFiles.length <= 0 && (
              <PrimaryButton className='w-full ' onClick={handleUploadArtwork} {...buttonProps}>
                {i18n._('Upload Artwork')}
              </PrimaryButton>
            )}
            {artworkFiles.length > 0 && (
              <SecondaryButton
                className='w-full '
                color='primary'
                onClick={handleUploadArtwork}
                {...buttonProps}
              >
                {i18n._('View Artwork')}
              </SecondaryButton>
            )}
          </div>
        )}

        <div>
          <PrimaryButton
            type='submit'
            // loading={formState.isSubmitting}
            className={`add-to-cart w-full ${!requiresArtwork ? 'mt-9' : ''}`}
            disabled={
              (requiresArtwork && !artworkFiles.length) ||
              (!!selectedVariantDesignId && !ppProjectId)
            }
            loading={formState.isSubmitting}
            {...buttonProps}
          >
            {i18n._(/* i18n */ 'Add to Cart')}
          </PrimaryButton>

          {additionalButtons}
        </div>
        {/* <AddProductsToCartButton fullWidth product={product} /> */}

        <ApolloCartErrorAlert error={error} />

        {data?.addProductsToCart?.user_errors.map((e) => (
          <Box key={e?.code}>
            <Alert severity='error'>{e?.message}</Alert>
          </Box>
        ))}

        <FileUploadModal
          modalId='fileUploadModal'
          isDiplayed={isDisplayFileUploadModal}
          setIsDisplayed={setIsDisplayFileUploadModal}
          setUploadedFile={setArtworkFiles}
          files={artworkFiles}
          proofOption={proofOption}
          setProofOption={setProofOption}
          artworkComment={artworkComment}
          setArtworkComment={setArtworkComment}
          callback={callback}
        />
        <ErrorPrompt error={attachError ?? error} />
      </Box>
      <SwipeableDrawer
        anchor='right'
        onClose={() => {
          setDrawer({ id: false })
          setAddedToCartDrawer(false)
        }}
        onOpen={() => {}}
        open={drawer.id === 'added_to_cart'}
        classes={{
          paper: 'rounded-l-[10px] max-w-[520px]',
        }}
      >
        <AddedToCart
          sku={variables.sku}
          onClose={() => {
            setDrawer({ id: false })
            setAddedToCartDrawer(false)
          }}
        />
      </SwipeableDrawer>
    </>
  )
}
